import { createContext, type JSX, type ReactNode, useContext, useMemo } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, DEFAULT_NAMESPACE, type SupportedLocale } from './config.js';
import { type CreateI18nOptions, createI18n } from './i18n.js';

// ---------------------------------------------------------------------------
// Locale context
// ---------------------------------------------------------------------------

interface LocaleContextValue {
  locale: SupportedLocale;
}

const LocaleContext = createContext<LocaleContextValue>({ locale: DEFAULT_LOCALE });

// ---------------------------------------------------------------------------
// I18nProvider
// ---------------------------------------------------------------------------

export interface I18nProviderProps {
  locale: SupportedLocale;
  children: ReactNode;
  i18nOptions?: Omit<CreateI18nOptions, 'locale'>;
}

/**
 * Wraps the subtree with a configured i18next instance and exposes the active
 * locale via `useLocale()`.
 *
 * Mount once near the top of your app, below the TanStack Router/Query
 * providers and above any component that calls `useT()` or `useLocale()`.
 */
export function I18nProvider({ locale, children, i18nOptions }: I18nProviderProps): JSX.Element {
  // Re-create the instance only when the locale changes. Extra resources
  // are expected to be stable references.
  // biome-ignore lint/correctness/useExhaustiveDependencies: i18nOptions is deliberately excluded — instance re-creation is locale-driven only.
  const instance = useMemo(() => createI18n({ ...i18nOptions, locale }), [locale]);

  const localeValue = useMemo<LocaleContextValue>(() => ({ locale }), [locale]);

  return (
    <LocaleContext.Provider value={localeValue}>
      <I18nextProvider i18n={instance}>{children}</I18nextProvider>
    </LocaleContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Thin wrapper around `useTranslation` from react-i18next.
 * Defaults to the 'common' namespace.
 *
 * @example
 *   const { t } = useT();
 *   return <span>{t('actions.save')}</span>;
 */
export function useT(namespace: string = DEFAULT_NAMESPACE) {
  return useTranslation(namespace);
}

/**
 * Returns the active locale resolved from the nearest `I18nProvider`.
 *
 * @example
 *   const locale = useLocale();  // 'en' | 'bg' | 'de' | 'fr'
 */
export function useLocale(): SupportedLocale {
  return useContext(LocaleContext).locale;
}
